"use client";

import React, { memo, useCallback, useMemo } from "react";
import { type MeiliThought } from "@/generated/fastapi";
import { SearchHitRendererProps } from "@/features/search";
import { ThoughtItem } from "@/features/thoughts";
import { useUserChoices, useInterimStateManager } from "@/state/globalStore";
import { useSituationsQuery } from "@/features/situations";
import { PROFILE_SHAPING_LABELS } from "@/features/profile-shaping/config/actions";

type MyInfiniteHitThoughtProps = SearchHitRendererProps<MeiliThought>;

function MyInfiniteHitThought({
  hit,
  config,
  onDeleteSearchResultAction,
  onUpdateSearchResultAction,
}: MyInfiniteHitThoughtProps) {
  const { isSelectedThought, toggleThoughtSelection } = useUserChoices();
  const { setInterimState } = useInterimStateManager();
  const situationsQuery = useSituationsQuery();
  const thought = hit as MeiliThought;
  const validSituationIds = useMemo(
    () =>
      new Set((situationsQuery.data ?? []).map((situation) => situation.id)),
    [situationsQuery.data],
  );
  const needsAnchor =
    !thought.situation_id ||
    (situationsQuery.isSuccess && !validSituationIds.has(thought.situation_id));

  const handleThoughtReassign = useCallback(
    async (selectedThought: MeiliThought) => {
      setInterimState("reassign_thought", {
        isOpen: true,
        values: [
          {
            objectID: selectedThought.objectID,
            situation_id: selectedThought.situation_id,
            prototype: selectedThought.prototype,
          },
        ],
      });
    },
    [setInterimState],
  );
  const handleReflectThought = useCallback(
    async (selectedThought: MeiliThought) => {
      setInterimState("reflect_thought", {
        isOpen: true,
        passedObject: selectedThought,
      });
    },
    [setInterimState],
  );
  const handleTraceOrigin = useCallback(
    async (selectedThought: MeiliThought) => {
      setInterimState("trace_origin", {
        isOpen: true,
        passedObject: {
          originId: selectedThought.origin_id,
        },
      });
    },
    [setInterimState],
  );

  const handleToggleSelectThought = useCallback(() => {
    toggleThoughtSelection({
      prototypeCategory: thought.prototype,
      situationId: thought.situation_id,
      thoughtId: thought.objectID,
    });
  }, [
    thought.objectID,
    thought.prototype,
    thought.situation_id,
    toggleThoughtSelection,
  ]);

  return (
    <ThoughtItem
      config={config}
      hit={hit}
      onDeleteSearchResultAction={(thoughtId) =>
        onDeleteSearchResultAction?.(thoughtId)
      }
      onUpdateSearchResultAction={onUpdateSearchResultAction}
      onReflectAction={handleReflectThought}
      onTraceOriginAction={handleTraceOrigin}
      selection={{
        isSelected: isSelectedThought(thought.objectID),
        onToggle: handleToggleSelectThought,
        humanReadable: "Select thought for batch operations",
      }}
      anchorAction={{
        onClick: handleThoughtReassign,
        icon: "anchor",
        humanReadable: needsAnchor
          ? "Anchor thought to a situation"
          : "Anchor thought to another situation",
        actionDescription: PROFILE_SHAPING_LABELS.anchorToSituation,
      }}
      primaryAction={{
        onClick: handleReflectThought,
        icon: "reflect_thoughts",
        humanReadable: "Reflect on this thought",
        actionDescription: "Reflect",
        color: "moveon.primary",
      }}
    />
  );
}

export default memo(MyInfiniteHitThought);
